<script setup>
/**
 * ImmersiveCatalogView
 * Route component for the immersive ("אימרסיבי") catalog experience. Reuses the
 * catalog store bootstrap (idempotent — shares data with the normal catalog),
 * builds a flat ordered feed, and drives the ImmersiveCatalog overlay. Works on
 * mobile and desktop (centered, mobile-first).
 */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '../../app/store/auth.js'
import { useCatalogStore } from '../../app/store/catalog.js'
import { ROUTES } from '../../core/config/constants.js'
import { useImmersiveCatalogNav } from '../../core/composables/useImmersiveCatalogNav.js'
import { useImmersiveTheme } from '../../core/composables/useImmersiveTheme.js'
import ImmersiveCatalog from '../components/catalog/immersive/ImmersiveCatalog.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const catalogStore = useCatalogStore()

// ── Agent/admin on-behalf context ────────────────────────────────
// Entered via AGENTS_CATALOG_IMMERSIVE (agents/catalog/:clientMakat/immersive).
// The agent catalog owns client loading, authorization and the agent-mode
// store bootstrap; this view only ever REUSES that context. When it's missing
// (deep link, refresh), bounce to the agent catalog instead of duplicating
// its auth/loading logic here.
const isAgentContext = computed(() => route.name === ROUTES.AGENTS_CATALOG_IMMERSIVE)

function hasAgentCatalogContext() {
  return (
    catalogStore.catalogMode === 'agent' &&
    String(catalogStore.linkedClient?.makat ?? '') === String(route.params.clientMakat ?? '')
  )
}

function backToAgentCatalog() {
  // Keep params + query (agentOverride) so the KeepAlive'd AgentCatalogView
  // reactivates with an unchanged context (no reload, filters preserved).
  router.replace({
    name: ROUTES.AGENTS_CATALOG,
    params: { clientMakat: route.params.clientMakat },
    query: route.query,
  })
}

// Theme the pre-ready fallback to match the persisted immersive theme.
const { theme } = useImmersiveTheme()

const { isBootstrapping, bootstrapError } = storeToRefs(catalogStore)

const {
  orderedItems,
  departmentsForJump,
  firstIndexOfDepartment,
  firstIndexOfDeptSub,
  subJumpTargets,
  subsOfDepartment,
} = useImmersiveCatalogNav()

const immersiveRef = ref(null)
const currentIndex = ref(0)

const total = computed(() => orderedItems.value.length)
const currentItem = computed(() => orderedItems.value[currentIndex.value] || null)
// Neighbors feed the overlay's three-pane swipe track (prev / current / next).
const prevItem = computed(() => orderedItems.value[currentIndex.value - 1] || null)
const nextItem = computed(() => orderedItems.value[currentIndex.value + 1] || null)

// Warm the ±2 neighbor images (±1 are already mounted on the track) so fast
// consecutive swipes never land on a blank pane on slow field networks.
watch(
  currentIndex,
  (i) => {
    for (const j of [i - 2, i + 2]) {
      const url = orderedItems.value[j]?.image
      if (typeof url === 'string' && url.trim().length > 0) {
        const img = new window.Image()
        img.src = url
      }
    }
  },
  { immediate: true }
)

// Hide prices for guests and clients with prices disabled (mirrors CatalogView).
// In agent context the LINKED client's flag decides (mirrors AgentCatalogView).
const showPrices = computed(() => {
  if (isAgentContext.value) {
    return catalogStore.linkedClient?.prices !== false
  }
  if (!authStore.isAuthenticated) return false
  const client = authStore.user?.client
  if (client && client.prices === false) return false
  return true
})

// Cart access mirrors the app shell's role gate (admin / client / agent).
const showCart = computed(() => {
  const role = authStore.userRole
  return role === 'admin' || role === 'client' || role === 'agent'
})

const isReady = computed(() => !isBootstrapping.value && total.value > 0)
const isEmpty = computed(() => !isBootstrapping.value && total.value === 0)

// Keep the index in range when the feed shrinks/loads.
watch(total, (len) => {
  if (currentIndex.value > len - 1) currentIndex.value = Math.max(0, len - 1)
})

// Start the feed at the catalog's currently-selected department / sub-department
// (so the immersive switch continues from where the user was). One-time, as soon
// as the ordered feed is first available. Promo virtual dept -> start at 0.
const startApplied = ref(false)
watch(
  orderedItems,
  (items) => {
    if (startApplied.value || items.length === 0) return
    startApplied.value = true
    const deptId = catalogStore.selectedDepartmentId
    if (!deptId || deptId === catalogStore.PROMO_DEPARTMENT_ID) return
    const idx = firstIndexOfDeptSub(deptId, catalogStore.selectedSubDepartmentId)
    if (idx > 0) currentIndex.value = idx
  },
  { immediate: true }
)

// ── Navigation ──────────────────────────────────────────────────
// The overlay's gesture engine animates the transition and only emits when a
// neighbor exists; the edge checks here are a safety net (bounce = spring nudge).
function goNext() {
  if (currentIndex.value < total.value - 1) {
    currentIndex.value++
  } else {
    immersiveRef.value?.bounce('next')
  }
}

function goPrev() {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    immersiveRef.value?.bounce('prev')
  }
}

function jumpToDepartment(deptId) {
  const idx = firstIndexOfDepartment(deptId)
  if (idx < 0 || idx === currentIndex.value) return
  currentIndex.value = idx
}

// Prev/next sub-category (within the current department) for the overlay's
// double-chevron jump buttons; -1 hides the corresponding arrow.
const subJump = computed(() => subJumpTargets(currentIndex.value))

// All subs of the current department for the sub-category selector sheet.
const currentSubs = computed(() => subsOfDepartment(currentItem.value?.department))

function jumpToIndex(idx) {
  if (typeof idx !== 'number' || idx < 0 || idx > total.value - 1) return
  currentIndex.value = idx
}

// Sync the normal catalog to the item the user is currently viewing, so exiting
// immersive lands them on the right department / sub-department page.
function syncCatalogToCurrentItem() {
  const it = currentItem.value
  if (!it || !it.department) return
  const deptEntry = departmentsForJump.value.find((d) => d.id === it.department)
  const deptName =
    deptEntry?.name || catalogStore.departments.find((d) => d.id === it.department)?.name || ''
  if (it.subDepartment) {
    const subs = catalogStore.subsByDepartmentId.get(it.department) || []
    const sub = subs.find((s) => s.id === it.subDepartment)
    catalogStore.selectSubDepartment(it.subDepartment, sub?.name || '', it.department, deptName)
  } else {
    catalogStore.selectDepartment(it.department, deptName)
  }
}

// Exit -> route the regular catalog (client or agent) to the last-viewed item's page.
function close() {
  syncCatalogToCurrentItem()
  if (isAgentContext.value) {
    backToAgentCatalog()
  } else {
    router.replace({ name: ROUTES.CATALOG })
  }
}

// ── Lifecycle ───────────────────────────────────────────────────
onMounted(() => {
  // Agent/admin on-behalf flow: the agent catalog already bootstrapped the
  // store in agent mode for this client — reuse it as-is (re-bootstrapping
  // with the logged-in user's profile would swap the catalog out from under
  // us). Missing context (deep link / refresh) → back to the agent catalog,
  // which owns client loading + authorization and re-applies agentOverride.
  if (isAgentContext.value) {
    if (!hasAgentCatalogContext()) {
      backToAgentCatalog()
      return
    }
    // Sub-department names for the exit-sync (idempotent, usually loaded).
    catalogStore.loadAllSubs().catch(() => {})
    return
  }

  // Sub-client gate: clients with multiple sub-clients must pick a working
  // client before the right assortment/prices load. Resolve that in the normal
  // catalog (which owns the picker), then they can re-enter immersive.
  // Mirror CatalogView's gate EXACTLY — count DEDUPED candidates (main client +
  // sub_clients, unique by makat) and only redirect when a real choice exists
  // (> 1); a looser `subs.length > 0` would permanently bounce users whose
  // sub_clients dedupe to a single candidate (the picker never shows there).
  const profile = authStore.user?.profile
  let candidateCount = 0
  if (authStore.userRole === 'client' && profile) {
    const seen = new Set()
    if (profile.client_makat) seen.add(String(profile.client_makat))
    const subs = Array.isArray(profile.sub_clients) ? profile.sub_clients : []
    for (const sc of subs) {
      if (sc?.makat) seen.add(String(sc.makat))
    }
    candidateCount = seen.size
  }
  if (!authStore.activeClientSelected && candidateCount > 1) {
    router.replace({ name: ROUTES.CATALOG })
    return
  }

  const userProfile = authStore.isAuthenticated ? authStore.user.profile : null
  catalogStore
    .bootstrap(userProfile)
    .then(() => catalogStore.loadAllSubs())
    .catch((err) => {
      console.error('[ImmersiveCatalogView] Bootstrap failed:', err)
    })
})

// Mirror AgentCatalogView's leave guard: leaving the agent catalog flow
// entirely (sidebar nav, checkout, …) must clear the temporary impersonation
// and the store's agent override so they don't leak into unrelated screens.
// Going back to the agent catalog (or its child routes) keeps them intact.
onBeforeRouteLeave((to) => {
  if (!isAgentContext.value) return
  const agentFlowRoutes = [
    ROUTES.AGENTS_CATALOG,
    ROUTES.AGENTS_PROMOTION_DETAIL,
    ROUTES.AGENTS_QUANTITY_DISCOUNT,
    ROUTES.AGENTS_NOT_IN_ASSORTMENT,
    ROUTES.AGENTS_ASSORTMENT_REQUEST,
  ]
  if (!agentFlowRoutes.includes(to.name)) {
    authStore.clearTemporaryAgentOverride()
    catalogStore.clearAgentOverride()
  }
})

// Safety net: also sync the catalog position when leaving via browser back or any
// other route change (close() handles the explicit X / Esc exit).
onUnmounted(() => {
  syncCatalogToCurrentItem()
})
</script>

<template>
  <div class="immersive-route">
    <!-- Ready: the immersive overlay -->
    <ImmersiveCatalog
      v-if="isReady"
      ref="immersiveRef"
      :item="currentItem"
      :prev-item="prevItem"
      :next-item="nextItem"
      :index="currentIndex"
      :total="total"
      :departments="departmentsForJump"
      :sub-jump="subJump"
      :subs="currentSubs"
      :show-prices="showPrices"
      :show-cart="showCart"
      @next="goNext"
      @prev="goPrev"
      @jump="jumpToDepartment"
      @jump-index="jumpToIndex"
      @close="close"
    />

    <!-- Loading / empty / error fall-back states -->
    <Teleport v-if="!isReady" to="body">
      <div class="imm-state" :class="{ 'theme-light': theme === 'light' }" dir="rtl">
        <button class="imm-state-close" type="button" aria-label="סגירה" @click="close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <template v-if="bootstrapError">
          <div class="imm-state-icon imm-state-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <p class="imm-state-text">שגיאה בטעינת הקטלוג</p>
        </template>

        <template v-else-if="isEmpty">
          <div class="imm-state-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <p class="imm-state-text">אין פריטים להצגה</p>
        </template>

        <template v-else>
          <div class="imm-state-spinner"></div>
          <p class="imm-state-text">טוען קטלוג…</p>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.immersive-route {
  height: 100%;
}

.imm-state {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background:
    radial-gradient(
      120% 80% at 50% 0%,
      var(--imm-state-glow, rgba(59, 130, 246, 0.16)) 0%,
      rgba(10, 15, 26, 0) 55%
    ),
    var(--imm-bg-base, #0a0f1a);
  color: var(--imm-text, #ffffff);
  padding: 24px;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
}

/* Light theme mirrors the immersive overlay so the loading/empty/error screen
   matches the user's persisted choice (no dark flash before content loads). */
.imm-state.theme-light {
  --imm-bg-base: #f4f7fb;
  --imm-text: #0f172a;
  --imm-text-soft: rgba(15, 23, 42, 0.78);
  --imm-text-faint: rgba(15, 23, 42, 0.4);
  --imm-glass-bg: rgba(255, 255, 255, 0.7);
  --imm-glass-border: rgba(15, 23, 42, 0.14);
  --imm-icon: #0f172a;
  --imm-accent: #2563eb;
  --imm-error: #dc2626;
  --imm-field-border: rgba(15, 23, 42, 0.18);
  --imm-state-glow: rgba(40, 53, 72, 0.12);
}

.imm-state-close {
  position: absolute;
  top: calc(14px + env(safe-area-inset-top, 0px));
  inset-inline-end: 16px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--imm-glass-border, rgba(255, 255, 255, 0.18));
  background: var(--imm-glass-bg, rgba(15, 23, 42, 0.55));
  color: var(--imm-icon, #ffffff);
  cursor: pointer;
}

.imm-state-close svg {
  width: 22px;
  height: 22px;
}

.imm-state-icon {
  color: var(--imm-text-faint, rgba(255, 255, 255, 0.35));
}

.imm-state-icon svg {
  width: 64px;
  height: 64px;
}

.imm-state-error {
  color: var(--imm-error, #f87171);
}

.imm-state-text {
  font-size: 1rem;
  font-weight: 600;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.8));
  margin: 0;
}

.imm-state-spinner {
  width: 44px;
  height: 44px;
  border: 4px solid var(--imm-field-border, rgba(255, 255, 255, 0.18));
  border-top-color: var(--imm-accent, #60a5fa);
  border-radius: 50%;
  animation: imm-spin 0.8s linear infinite;
}

@keyframes imm-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .imm-state-spinner {
    animation-duration: 1.6s;
  }
}
</style>
